import { withSiteTitle } from "@/lib/page-title";

export type MetaTag = Record<string, string>;

export interface SeoMetaData {
  title: string;
  documentTitle?: string | null;
  description?: string | null;
  socialTitle?: string | null;
  socialDescription?: string | null;
  canonicalUrl?: string | null;
  imageUrl?: string | null;
}

export interface OpenGraphMetaOptions {
  type?: string | null;
  siteName?: string | null;
  image?:
    | false
    | {
        width?: string | null;
        height?: string | null;
        alt?: string | null;
      };
}

export interface TwitterMetaOptions {
  card?: string | null;
  includeTitle?: boolean;
  includeDescription?: boolean;
  includeImage?: boolean;
}

export interface SeoMetaOptions {
  robots?: string | null;
  openGraph?: false | OpenGraphMetaOptions;
  twitter?: false | TwitterMetaOptions;
}

export const PRIVATE_PAGE_ROBOTS = "noindex,nofollow" as const;
export const ROLLOUT_NOINDEX_ROBOTS = "noindex,follow" as const;

export function buildPrivatePageMetaTags(title: string): MetaTag[] {
  return buildSeoMetaTags(
    { title },
    {
      robots: PRIVATE_PAGE_ROBOTS,
      openGraph: false,
      twitter: false,
    },
  );
}

export function buildSeoMetaTags(data: SeoMetaData, options: SeoMetaOptions = {}): MetaTag[] {
  const meta: MetaTag[] = [{ title: withSiteTitle(data.documentTitle ?? data.title) }];

  if (data.description) {
    meta.push({ name: "description", content: data.description });
  }
  if (options.robots) {
    meta.push({ name: "robots", content: options.robots });
  }

  if (options.openGraph !== false && options.openGraph) {
    appendOpenGraphMetaTags(meta, data, options.openGraph);
  }
  if (options.twitter !== false && options.twitter) {
    appendTwitterMetaTags(meta, data, options.twitter);
  }

  return meta;
}

function appendOpenGraphMetaTags(
  meta: MetaTag[],
  data: SeoMetaData,
  options: OpenGraphMetaOptions,
) {
  meta.push({ property: "og:title", content: data.socialTitle ?? data.title });

  const description = data.socialDescription ?? data.description;
  if (description) {
    meta.push({ property: "og:description", content: description });
  }
  if (data.canonicalUrl) {
    meta.push({ property: "og:url", content: data.canonicalUrl });
  }
  if (options.type) {
    meta.push({ property: "og:type", content: options.type });
  }
  if (options.siteName) {
    meta.push({ property: "og:site_name", content: options.siteName });
  }

  appendOpenGraphImageMetaTags(meta, data, options.image);
}

function appendTwitterMetaTags(meta: MetaTag[], data: SeoMetaData, options: TwitterMetaOptions) {
  if (options.card) {
    meta.push({ name: "twitter:card", content: options.card });
  }
  if (options.includeTitle !== false) {
    meta.push({ name: "twitter:title", content: data.socialTitle ?? data.title });
  }
  const description = data.socialDescription ?? data.description;
  if (options.includeDescription !== false && description) {
    meta.push({ name: "twitter:description", content: description });
  }
  if (options.includeImage !== false && data.imageUrl) {
    meta.push({ name: "twitter:image", content: data.imageUrl });
  }
}
import { appendOpenGraphImageMetaTags } from "@/lib/append-open-graph-image-meta-tags";
